import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Injector } from '@angular/core';

import { KeyExistsPipe, TranslateService } from '@jht/brand-translate';
import { Insights } from '@src/app/insights/insights';
import {
    ArticleSchema,
    OrganizationSchema,
    SchemaImage,
    Organization,
} from './schema.model';

@Injectable({
    providedIn: 'root',
})
export class SchemaService {
    constructor(
        @Inject(DOCUMENT) private readonly document: Document,
        private readonly injector: Injector,
    ) {}

    private _translate?: TranslateService;
    private _keyExists?: KeyExistsPipe;

    private get translate(): TranslateService {
        return (this._translate ??= this.injector.get(TranslateService));
    }

    private get keyExists(): KeyExistsPipe {
        return (this._keyExists ??= this.injector.get(KeyExistsPipe));
    }

    private readonly schemaIds = {
        organization: 'organizationSchema',
        article: 'articleSchema',
    } as const;

    addOrganizationSchema(): void {
        const schema: OrganizationSchema = {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: this.getOrganizationName(),
            url:
                this.translateIfExists('SCHEMA_ORGANIZATION_URL') ??
                this.document.location.origin,
            legalName: this.translateIfExists('SCHEMA_ORGANIZATION_LEGAL_NAME'),
            description: this.translateIfExists(
                'SCHEMA_ORGANIZATION_DESCRIPTION',
            ),
            foundingDate: this.translateIfExists(
                'SCHEMA_ORGANIZATION_FOUNDING_DATE',
            ),
            logo: this.getOrganizationLogo(),
        };

        this.addSchemaScript(this.schemaIds.organization, schema);
    }

    addArticleSchema(insights: Insights): void {
        const pageUrl = new URL(
            `/insights/${insights.slug}`,
            this.document.location.origin,
        ).toString();

        const schema: ArticleSchema = {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: insights.title,
            datePublished: this.toIso(insights.date),
            dateModified: this.toIso(insights.date),
            isAccessibleForFree: true,
            url: pageUrl,
            mainEntityOfPage: {
                '@type': 'WebPage',
                '@id': pageUrl,
            },
            publisher: this.getOrganization(),
            description: insights['schema-description'] || insights.title,
        };

        if (insights['author-info']) {
            schema.author = {
                '@type': 'Person',
                name: insights['author-info'],
                image: this.normalizeImage(insights['author-picture']),
            };
        } else {
            schema.author = this.getOrganization();
        }

        if (insights.location) {
            schema.contentLocation = {
                '@type': 'Place',
                name: insights.location,
            };
        }

        this.addSchemaScript(this.schemaIds.article, schema);
    }

    private getOrganization(): Organization {
        return {
            '@type': 'Organization',
            name: this.getOrganizationName(),
            logo: this.getOrganizationLogo(),
            url: this.translateIfExists('SCHEMA_ORGANIZATION_URL'),
        };
    }

    private normalizeImage(image?: string): string[] {
        if (!image) {
            return [];
        }

        return [this.getAbsoluteUrl(image)];
    }

    private getAbsoluteUrl(url?: string): string {
        if (!url) {
            return this.document.location.origin;
        }

        if (url.startsWith('http')) {
            return url;
        }

        return new URL(url, this.document.location.origin).toString();
    }

    private toIso(date?: string): string {
        return date ? new Date(date).toISOString() : '';
    }

    private getPageLanguage(): string {
        return this.document.documentElement.lang?.trim() || 'en';
    }

    private getOrganizationName(): string {
        return this.translateIfExists('SCHEMA_ORGANIZATION_NAME') ?? '';
    }

    private getOrganizationLogo(): SchemaImage {
        const logo = this.translateIfExists('SCHEMA_ORGANIZATION_LOGO');

        return {
            '@type': 'ImageObject',
            url: this.getAbsoluteUrl(logo),
        };
    }

    private translateIfExists(key: string): string | undefined {
        return this.keyExists.transform(key)
            ? this.translate.instant(key)
            : undefined;
    }

    private addSchemaScript(
        id: string,
        schema: ArticleSchema | OrganizationSchema,
    ): void {
        this.removeSchemaScript(id);

        const script = this.document.createElement('script');

        script.id = id;
        script.type = 'application/ld+json';
        script.text = JSON.stringify(schema);

        this.document.head.appendChild(script);
    }

    private removeSchemaScript(id: string): void {
        this.document.getElementById(id)?.remove();
    }

    removeArticleSchema(): void {
        this.removeSchemaScript(this.schemaIds.article);
    }
}
